import { useCallback, useEffect, useState } from 'react'
import api from '@/services/api'
import { mapDealsFromApi } from '@/adapters/deals'
import { financeService } from '@/services/financeService'
import {
  buildChamadoPendencias,
  buildDealPendencias,
  mergeOperacaoPendencias,
  type OperacaoPendenciaItem,
} from '@/components/Dashboard/operacaoPendencias.util'
import { useApiSessionReady } from '@/hooks/useApiSessionReady'

type UseOperacaoPendenciasResult = {
  items: OperacaoPendenciaItem[]
  totalCount: number
  loading: boolean
  refresh: () => void
}

export function useOperacaoPendencias(): UseOperacaoPendenciasResult {
  const sessionReady = useApiSessionReady()
  const [items, setItems] = useState<OperacaoPendenciaItem[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [chamadosRes, dealsRes] = await Promise.allSettled([
        financeService.listRequests({
          page: 1,
          limit: 50,
          queueLifecycle: 'ativos',
          area: 'COMERCIAL',
        }),
        api.get('/deals', {
          params: { page: 1, limit: 100, sortBy: 'updatedAt', sortOrder: 'desc' },
        }),
      ])

      const chamadoRows =
        chamadosRes.status === 'fulfilled' ? chamadosRes.value.data : []
      const deals =
        dealsRes.status === 'fulfilled'
          ? mapDealsFromApi(dealsRes.value.data?.data ?? dealsRes.value.data ?? [])
          : []

      const chamadoItems = buildChamadoPendencias(chamadoRows)
      const dealItems = buildDealPendencias(deals)
      const merged = mergeOperacaoPendencias(chamadoItems, dealItems, 5)

      setItems(merged)
      setTotalCount(chamadoItems.length + dealItems.length)
    } catch {
      setItems([])
      setTotalCount(0)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!sessionReady) return
    void load()
  }, [load, sessionReady])

  return { items, totalCount, loading, refresh: load }
}
